import React, { useEffect, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, pick, uniqBy } from 'lodash-es';

import FormModalContent from './FormModalContent';
import ConfirmationModalContent from './ConfirmationModalContent';
import useScheduleExportDraft from './useScheduleExportDraft';
import PageModal from '../../../../Modals/PageModal';
import { toast } from '../../../../Toasts';
import api from '../../../api';
import { decorateUpsertExportRequestParams } from '../../../utils';
import { generateDateTimeSuffix } from '../../../../../utils/helpers';
import { addQuarters, startOfQuarter, today } from '../../../../../utils/date';
import { getGlobalSettings } from '../../../../../utils/global_settings';

const isCurrentUserRecipient = (recipients) => {
  const { currentUser } = getGlobalSettings();
  if (isEmpty(currentUser)) return false;

  return recipients.some((r) => r.id === currentUser.id);
};

const preselectCurrentUserAsRecipient = () => {
  const { currentUser } = getGlobalSettings();
  if (isEmpty(currentUser)) return [];

  // Save the same structure as response from the API
  return [
    {
      id: currentUser.id,
      kind: 'user',
      label: `${currentUser.fullName} (${currentUser.email})`,
      text: `${currentUser.fullName} (${currentUser.email})`,
      value: currentUser.email,
    },
  ];
};

const getUserAllowedDomains = () => {
  const { currentUser } = getGlobalSettings();
  if (isEmpty(currentUser)) return [];

  if (!isEmpty(currentUser.domainsList)) return currentUser.domainsList;

  // Fallback to extract domain from email if domains list is not available
  return currentUser.email ? [currentUser.email.split('@')[1]] : [];
};

const defaultExtraReportState = (report) => ({
  name: generateDateTimeSuffix(),
  schedule_expires_at: startOfQuarter(addQuarters(today(), 1)),
  frequency: 'weekly',
  recipients: report.email_notification ? preselectCurrentUserAsRecipient() : [],
});

const ScheduleExportModal = ({
  open,
  report,
  shouldResyncOnUpdate = false,
  onClose,
  onBackToEdit,
  onSuccess,
}) => {
  const allowedDomainsRef = useRef(getUserAllowedDomains());

  const [step, setStep] = useState('form'); // 'form' | 'confirmation'
  const [extraReportState, setExtraReportState] = useState(defaultExtraReportState(report));

  const [isProcessing, setIsProcessing] = useState(false);

  const { getDraft, setDraft, clearDraft } = useScheduleExportDraft(report);

  const userEditedRef = useRef(false);

  const extendedReport = useMemo(
    () => ({ ...report, ...extraReportState }),
    [report, extraReportState]
  );

  const handleExtraReportChange = (updater) => {
    userEditedRef.current = true;
    setExtraReportState(updater);
  };

  // X / overlay close discards the draft; the explicit "Edit Export" button keeps it.
  const handleClose = () => {
    clearDraft();
    onClose();
  };

  const handleConfirm = () => {
    setIsProcessing(true);

    const requestData = decorateUpsertExportRequestParams(report, {
      ...pick(extraReportState, ['name', 'schedule_expires_at', 'frequency']),
      recipient_emails: extraReportState.recipients.map((r) => r.value),
      email_notification: isCurrentUserRecipient(extraReportState.recipients),
    });

    const request = !report.id
      ? api.createCustomExport(requestData)
      : api.updateCustomExport(report.id, { ...requestData, no_resync: !shouldResyncOnUpdate });

    request
      .then(() => {
        setIsProcessing(false);
        clearDraft();
        onSuccess(requestData);
      })
      .catch(() => {
        setIsProcessing(false);
        toast.error('Oops! Something went wrong. Please, try again later');
      });
  };

  // Effects
  useEffect(() => {
    if (open) {
      // Restore the user's in-progress draft verbatim.
      const draft = getDraft();
      if (draft) {
        setExtraReportState(draft);
      } else {
        // No draft: if the report has been previously scheduled, populate with its values; otherwise defaults.
        const initialState = defaultExtraReportState(report);
        setExtraReportState({
          name: report?.id ? report.name : initialState.name,
          schedule_expires_at: report?.schedule_expires_at || initialState.schedule_expires_at,
          frequency: report?.frequency || initialState.frequency,
          recipients: uniqBy(initialState.recipients.concat(report?.recipients || []), 'value'),
        });
      }
    } else {
      const initialState = defaultExtraReportState(report);

      // Reset step to 'form' on modal close and clear extra report state
      setStep('form');
      setExtraReportState(initialState);
      userEditedRef.current = false;
    }
  }, [open]);

  // Persist the draft only for user-originated changes while the modal is open.
  useEffect(() => {
    if (open && userEditedRef.current) {
      setDraft(extraReportState);
    }
  }, [extraReportState, open]);

  return (
    <PageModal
      open={open}
      shouldCloseOnOverlayClick={false}
      closeButtonVersionButtonSize="lg"
      hideCloseButton={step === 'confirmation'}
      className={cx('schedule-export-modal', step)}
      header="Schedule Export"
      main={
        step === 'form' ? (
          <FormModalContent
            extraReportState={extraReportState}
            allowedDomains={allowedDomainsRef.current}
            setExtraReportState={handleExtraReportChange}
            setStep={setStep}
            onBackToEdit={onBackToEdit || onClose}
          />
        ) : (
          <ConfirmationModalContent
            isProcessing={isProcessing}
            report={extendedReport}
            setStep={setStep}
            onConfirm={handleConfirm}
          />
        )
      }
      onClose={handleClose}
    />
  );
};

ScheduleExportModal.propTypes = {
  open: T.bool.isRequired,
  report: T.object.isRequired,
  shouldResyncOnUpdate: T.bool,
  onClose: T.func.isRequired,
  onBackToEdit: T.func,
  onSuccess: T.func,
};

export default ScheduleExportModal;
